import type {
  ChartBlock,
  ChartKind,
  ChartLegendPlacement,
  ChartRow,
  ChartState,
  Severity,
} from "../types";
import { normalizeText } from "../../ui/utils";
import {
  CHART_HEIGHT,
  DOUGHNUT_LEGEND_ROW_PX,
  DOUGHNUT_PLOT_PX,
  resolveColor,
} from "./chartTheme";

const clean = (s: string | undefined): string | undefined => (s ? normalizeText(s) : s);

export interface NormalizedSeries {
  key: string;
  label: string;
  color: string;
}

export type LegendPlacement = "top" | "bottom" | "right" | "none";

export interface NormalizedChart {
  kind: ChartKind;
  title?: string;
  subtitle?: string;
  unit: string;
  stacked: boolean;
  legend: LegendPlacement;
  xLabel?: string;
  yLabel?: string;
  /** The x key. Names a category label for every kind except scatter, where it names a number. */
  categoryKey: string;
  /** Timeline only — the row height it will be rendered at, matching the reserved height. */
  timelineRowHeight?: number;
  /** Scatter only — the y, size, colour and point-label keys. See {@link ChartBlock}. */
  yKey?: string;
  zKey?: string;
  groupKey?: string;
  labelKey?: string;
  series: NormalizedSeries[];
  rows: Array<ChartRow & { fill?: string }>;
  singleSeries: boolean;
  total: number;
}

const LEGACY_KIND: Record<NonNullable<ChartBlock["chart"]>, ChartKind> = {
  donut: "doughnut",
  bar: "bar",
  line: "line",
};

export function isRadial(kind: ChartKind): boolean {
  return kind === "pie" || kind === "doughnut";
}

export function isHorizontal(kind: ChartKind): boolean {
  return kind === "horizontalBar";
}

/**
 * Scatter, the one kind whose x axis is a number rather than a label.
 *
 * Worth its own predicate because that single difference reaches three places: `normalizeChart`
 * must not stringify the x value, `chartHeight` must not reserve a category-label band, and
 * `ChartMessage` must not read an all-zero y series as an empty chart — a scatter where nothing
 * has drifted yet is a real result, not a missing one.
 */
export function isScatter(kind: ChartKind): boolean {
  return kind === "scatter";
}

/**
 * The scan timeline, which is not a plot of measures at all.
 *
 * Its rows are raw scans — a timestamp and a verdict — consumed whole by a component that does its
 * own day bucketing and positioning. So unlike every other kind, nothing here should be coerced:
 * stringifying the x would break the date parsing, and running the series loop over a status would
 * turn "passed" into 0.
 */
export function isScanTimeline(kind: ChartKind): boolean {
  return kind === "scanTimeline";
}

const RADIAL_LEGEND_ROW_PX = 28;
const RADIAL_LEGEND_GAP_PX = 12;
// The legend row renders outside the plot, so its height must be reserved separately or it shifts.
const CARTESIAN_LEGEND_PX = 26;

function hasExternalLegend(n: NormalizedChart): boolean {
  return !n.singleSeries && (n.legend === "top" || n.legend === "bottom");
}

const CORE_PLOT_PX = 180;

/**
 * Plot height for a chat scatter. Exported so ChatScatter passes this exact value rather than
 * leaning on the shared component's default — if the two drifted apart the reserved height would
 * silently stop matching the rendered one, which is the CLS this function exists to prevent.
 */
export const SCATTER_PLOT_PX = 280;
/** Its legend row plus the bubble size key, both of which it renders below the plot. */
const SCATTER_CHROME_PX = 52;

/**
 * Timeline geometry, mirroring ScanTimelineMatrix's own constants. Duplicated deliberately: this
 * has to be computable before the component mounts, which is the whole point of reserving height.
 */
const TIMELINE_HEADER_PX = 44 + 30; // plot top margin + the pass/fail legend above it
const TIMELINE_ROW_PX = 44; // the compact row height the chat panel asks for
const TIMELINE_FOOTER_PX = 40; // the "Time of day (UTC)" caption
/** Must match ChatScanTimeline's maxHeight, or the reserved box and the rendered card disagree. */
export const TIMELINE_ROWS_MAX_PX = 240;

export function longestCategory(n: NormalizedChart): number {
  return n.rows.reduce((m, r) => Math.max(m, String(r[n.categoryKey] ?? "").length), 0);
}

const LABEL_CHAR_PX = 6.4;
const ASSUMED_PLOT_W = 300;
const MAX_LABEL_LINES = 3;

export interface CategoryLabelLayout {
  angled: boolean;
  charsPerLine: number;
  lines: number;
}

export function categoryLabelLayout(n: NormalizedChart): CategoryLabelLayout {
  const count = Math.max(1, n.rows.length);
  if (n.kind === "bar" && count > 4) {
    return { angled: true, charsPerLine: 18, lines: 1 };
  }
  const perCategory = ASSUMED_PLOT_W / count;
  const charsPerLine = Math.max(6, Math.min(30, Math.floor(perCategory / LABEL_CHAR_PX)));
  const lines = Math.min(MAX_LABEL_LINES, Math.max(1, Math.ceil(longestCategory(n) / charsPerLine)));
  return { angled: false, charsPerLine, lines };
}

export function xAxisBand(n: NormalizedChart): number {
  if (isHorizontal(n.kind)) return n.xLabel ? 28 : 0;
  const { angled, lines } = categoryLabelLayout(n);
  const title = n.xLabel ? 16 : 0;
  if (angled) return 64 + title;
  return 12 + lines * 13 + title;
}

export function plotHeight(n: NormalizedChart): number {
  if (isHorizontal(n.kind)) {
    return Math.min(360, Math.max(160, n.rows.length * 34 + 52));
  }
  return CORE_PLOT_PX + xAxisBand(n);
}

// Height is computed BEFORE render and reserved, so the skeleton->chart swap never shifts layout (no CLS).
export function chartHeight(n: NormalizedChart): number {
  // The rows are capped and scroll inside that cap, so the card no longer grows with the data —
  // which also makes this reservation exact rather than estimated. It still shrinks for a short
  // history, so a control scanned on two days does not reserve empty space below its rows.
  if (isScanTimeline(n.kind)) {
    const days = new Set(
      n.rows.map((r) => String(r[n.categoryKey] ?? "").slice(0, 10)).filter(Boolean),
    );
    const rows = Math.min(
      Math.max(1, days.size) * TIMELINE_ROW_PX,
      TIMELINE_ROWS_MAX_PX,
    );
    return TIMELINE_HEADER_PX + rows + TIMELINE_FOOTER_PX;
  }
  // A scatter's x values are numbers, so there is no category-label band to reserve — asking
  // xAxisBand() to wrap "42.5" over three lines would reserve the wrong height and jump on swap.
  // Its height is fixed by the shared component instead of growing with the row count.
  if (isScatter(n.kind)) return SCATTER_PLOT_PX + SCATTER_CHROME_PX;
  // The doughnut is the platform donut, which reserves its own plot height and stacks its legend as
  // an auto-fit grid — two columns at the widths the chat panel actually has, hence the halved row
  // count. Estimated rather than measured: this only has to keep the lazy skeleton close enough that
  // the swap does not jump.
  if (n.kind === "doughnut") {
    if (n.legend === "none") return DOUGHNUT_PLOT_PX;
    return (
      DOUGHNUT_PLOT_PX
      + Math.ceil(n.rows.length / 2) * DOUGHNUT_LEGEND_ROW_PX
      + RADIAL_LEGEND_GAP_PX
    );
  }
  if (isRadial(n.kind)) {
    const box = CHART_HEIGHT.radial;
    if (n.legend === "none") return box;
    return box + n.rows.length * RADIAL_LEGEND_ROW_PX + RADIAL_LEGEND_GAP_PX;
  }
  return plotHeight(n) + (hasExternalLegend(n) ? CARTESIAN_LEGEND_PX : 0);
}

export function kindLabel(kind: ChartKind): string {
  switch (kind) {
    case "horizontalBar":
      return "horizontal bar";
    case "doughnut":
      return "doughnut";
    default:
      return kind;
  }
}

const KNOWN_CHART_STATES: ChartState[] = ["loading", "success", "empty", "nodata", "error"];

export function deriveState(spec: ChartBlock | null | undefined, loading?: boolean): ChartState {
  if (loading) return "loading";
  if (!spec) return "nodata";
  if (spec.state) {
    if (KNOWN_CHART_STATES.includes(spec.state)) return spec.state;
    // Unrecognized state falls back on whether data is present, so it never renders a blank chart.
    return Array.isArray(spec.data) && spec.data.length > 0 ? "success" : "nodata";
  }
  if (!Array.isArray(spec.data)) return "nodata";
  if (spec.data.length === 0) return "empty";
  return "success";
}

export function isAllZero(n: NormalizedChart): boolean {
  return n.rows.every((r) => n.series.every((s) => Number(r[s.key]) === 0));
}

function looksTemporal(labels: string[]): boolean {
  if (labels.length < 3) return false;
  const temporal =
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|q[1-4]\b|week|wk|day|mon|tue|wed|thu|fri|sat|sun|\d{4}|\d{1,2}[/-]\d{1,2})/i;
  const hits = labels.filter((l) => temporal.test(l.trim())).length;
  return hits >= Math.ceil(labels.length * 0.6);
}

function longestLabel(labels: string[]): number {
  return labels.reduce((max, l) => Math.max(max, l.length), 0);
}

export function autoKind(categoryKey: string, seriesCount: number, rows: ChartRow[]): ChartKind {
  const labels = rows.map((r) => String(r[categoryKey] ?? ""));
  const temporal = looksTemporal(labels);

  if (seriesCount > 1) return temporal ? "line" : "bar";
  if (temporal) return labels.length > 8 ? "area" : "line";
  if (labels.length <= 5 && longestLabel(labels) <= 10) return "doughnut";
  if (labels.length <= 6 && longestLabel(labels) <= 6) return "bar";
  return "horizontalBar";
}

function resolveLegend(
  pref: ChartLegendPlacement,
  kind: ChartKind,
  seriesCount: number,
): LegendPlacement {
  if (pref !== "auto") return pref;
  if (isRadial(kind)) return "right";
  return seriesCount > 1 ? "top" : "none";
}

function asSeverity(v: unknown): Severity | undefined {
  return typeof v === "string" &&
    ["critical", "high", "medium", "low", "info"].includes(v)
    ? (v as Severity)
    : undefined;
}

export function normalizeChart(spec: ChartBlock): NormalizedChart {
  const categoryKey = spec.xKey ?? "label";
  const unit = spec.unit ?? "";
  const rawRows = Array.isArray(spec.data) ? spec.data : [];

  const hasSeries = Array.isArray(spec.series) && spec.series.length > 0;
  const seriesDefs = hasSeries ? spec.series! : [{ key: "value", label: "Value" }];
  const series: NormalizedSeries[] = seriesDefs.map((s, i) => ({
    key: s.key,
    label: clean(s.label ?? s.key) ?? s.key,
    // Colour severity-named series by name, so dropping one does not reshuffle the rest.
    color: resolveColor({ color: s.color, label: s.label ?? s.key }, i),
  }));
  const singleSeries = series.length === 1;

  // Resolved before the row loop because the loop's treatment of the x value depends on it: a
  // scatter's x is a number and must survive as one. Only the *preference* is hoisted — the
  // "auto" fallback below still needs `rows`, and autoKind never returns scatter anyway.
  const kindPref = spec.kind ?? (spec.chart ? LEGACY_KIND[spec.chart] : "auto");
  const scatter = kindPref !== "auto" && isScatter(kindPref);
  const timeline = kindPref !== "auto" && isScanTimeline(kindPref);

  const rows = rawRows.map((raw, i) => {
    const r = raw && typeof raw === "object" ? raw : ({} as ChartRow);
    const row: ChartRow & { fill?: string } = { ...r };
    // A timeline's rows are raw scans, read whole by the component that draws them. Coercing them
    // here would destroy exactly what it needs: the timestamp becomes an unparseable string and the
    // verdict becomes 0.
    if (timeline) {
      return row;
    }
    if (scatter) {
      const x = Number(r[categoryKey]);
      row[categoryKey] = Number.isFinite(x) ? x : 0;
    } else {
      row[categoryKey] = normalizeText(String(r[categoryKey] ?? ""));
    }
    for (const s of series) {
      const v = Number(r[s.key]);
      row[s.key] = Number.isFinite(v) ? v : 0;
    }
    if (singleSeries) {
      row.fill = resolveColor(
        {
          color: typeof r.color === "string" ? r.color : undefined,
          severity: asSeverity(r.severity),
          // Nothing populates `severity` on a doughnut the model asked for, so read it off the
          // category label too — otherwise a findings-by-severity donut falls through to the
          // positional palette and comes out indigo/green beside a red/orange widget.
          label: row[categoryKey],
        },
        i,
      );
    }
    return row;
  });

  const kind = kindPref === "auto" ? autoKind(categoryKey, series.length, rows) : kindPref;

  const total = rows.reduce((sum, r) => {
    const v = Number(r[series[0].key]);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);

  return {
    kind,
    title: clean(spec.title),
    subtitle: clean(spec.subtitle),
    unit,
    stacked: !!spec.stacked,
    legend: resolveLegend(spec.legend ?? "auto", kind, series.length),
    xLabel: clean(spec.xLabel),
    yLabel: clean(spec.yLabel),
    categoryKey,
    /** Timeline geometry, so the adapter and the reserved height cannot disagree. */
    timelineRowHeight: TIMELINE_ROW_PX,
    yKey: spec.yKey,
    zKey: spec.zKey,
    groupKey: spec.groupKey,
    labelKey: spec.labelKey,
    series,
    rows,
    singleSeries,
    total,
  };
}

export function buildSrSummary(n: NormalizedChart): string {
  const head = `${n.title ? `${n.title}. ` : ""}${kindLabel(n.kind)} chart.`;
  const rows = n.rows.slice(0, 24).map((r) => {
    const label = String(r[n.categoryKey] ?? "");
    if (n.singleSeries) return `${label}: ${r[n.series[0].key]}${n.unit}`;
    const parts = n.series.map((s) => `${s.label} ${r[s.key]}${n.unit}`).join(", ");
    return `${label}: ${parts}`;
  });
  const more = n.rows.length > 24 ? `; and ${n.rows.length - 24} more` : "";
  return `${head} ${rows.join("; ")}${more}.`;
}
